import { gql, useQuery } from '@apollo/client';
import React from 'react';
import Button from '../../components/Button';
import SVGIcon from '../../components/SVGIcon';
import type {
  ExternalProfileButtons_UserFragment,
  ExternalProfilesQuery,
  ExternalProfilesQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import slackIcon from '../../icons/prefilled/slack.svg';
import teamsIcon from '../../icons/prefilled/teams.svg';

const EXTERNAL_PROFILES_QUERY = gql`
  query externalProfiles($userId: ID!) {
    microsoftIntegration: integration(platform: "microsoft") {
      id
    }

    slackIntegration: integration(platform: "slack") {
      externalId
    }

    slackUser(slabId: $userId) {
      id
    }
  }
`;

const USER_FRAGMENT = gql`
  fragment ExternalProfileButtons_user on User {
    id
    email
    identities {
      id
      externalId
      platform
    }
  }
`;

interface ExternalProfileButtonsProps {
  size: 'small' | 'regular';
  user: ExternalProfileButtons_UserFragment;
}

const ExternalProfileButtons = ({
  user,
  size,
}: ExternalProfileButtonsProps) => {
  const { data } = useQuery<
    ExternalProfilesQuery,
    ExternalProfilesQueryVariables
  >(EXTERNAL_PROFILES_QUERY, {
    variables: { userId: user.id },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const slackIdentity = user.identities?.find((i) => i?.platform === 'slack');
  const slackUserId = slackIdentity?.externalId || data?.slackUser?.id;
  const slackTeamId = data?.slackIntegration?.externalId;

  const slackButton =
    slackTeamId && slackUserId ? (
      <Button
        variant="secondary"
        size={size}
        to={`slack://user?team=${slackTeamId}&id=${slackUserId}`}
        icon={slackIcon}
      >
        Slack
      </Button>
    ) : null;

  // https://docs.microsoft.com/en-us/microsoftteams/platform/concepts/build-and-test/deep-links#deep-linking-to-a-chat
  const teamsButton = data?.microsoftIntegration ? (
    <Button
      variant="secondary"
      size={size}
      to={`https://teams.microsoft.com/l/chat/0/0?users=${user.email ?? ''}`}
      target="_blank"
      icon={
        <SVGIcon size={size} coloringWith="fill">
          {teamsIcon}
        </SVGIcon>
      }
    >
      Teams
    </Button>
  ) : null;

  return (
    <>
      {slackButton}
      {teamsButton}
    </>
  );
};

ExternalProfileButtons.fragments = {
  user: USER_FRAGMENT,
};

export default ExternalProfileButtons;
